import { CurrencyInput } from '@dealstack/frontend/app/pages/Valuations/components/CurrencyInput/CurrencyInput';
import { AlignRight } from '@dealstack/frontend/app/pages/Valuations/components/styled';
import {
	ActionIconSizedPlaceholder,
	AdjustmentRow,
	FlexContainer,
	Header,
	HeaderBottom,
	HeaderFooterRowItem,
	HeaderTop,
	InputAdjustmentRowConvertedAmount,
	InputAdjustmentRowItem,
	InputAdjustmentRowTotal,
	InputRow,
	RowContainer,
	StyledButton,
	StyledCard,
	Table,
	TableCell,
	TableCellContent,
	TableHeader,
	TableHeaderTitle,
	TableRow,
	TotalRow,
} from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/ValuationsInputTable/styled';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatDecimal } from '@dealstack/shared/lib/format/number';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	CreateValuationInputAdjustmentDto,
	CurrencyType,
	ValuationInputAdjustmentWithClassifierRes,
	ValuationInputType,
	ValuationNumberFormat,
} from '@dealstack/shared/lib/generated';
import { VALUATION_ADJUSTMENT_CLASSIFIERS } from '@dealstack/shared/lib/common/business/valuationClassifiers';
import {
	makeComplexDataTestId,
	TEST_ELEMENT_IDS,
	VALUATION_ELEMENT_ID,
} from '@dealstack/shared/tests/testElementIds';
import { ActionIcon, Flex } from '@mantine/core';
import React from 'react';
import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { uuidv7 } from 'uuidv7';
import { CreateInputAdjustmentTableForm } from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { ValuationInputDetails } from '@dealstack/frontend/app/pages/Valuations/shared/valuationInputDetails.types';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface ValuationsInputTableProps {
	selectedValuationInputId: string;
	selectedValuationInputDetails: ValuationInputDetails;
	defaultCurrency: CurrencyType;
	defaultNumberFormat: ValuationNumberFormat;
	currentNumberFactor: number;
	createInputAdjustment: (params: {
		valuationInputId: string;
		data: CreateValuationInputAdjustmentDto;
	}) => Promise<ValuationInputAdjustmentWithClassifierRes | undefined>;
	deleteInputAdjustment: (params: {
		valuationInputAdjustmentId: string;
		valuationInputId: string;
	}) => void;
}

export const ValuationsInputTable: React.FC<ValuationsInputTableProps> = ({
	selectedValuationInputId,
	selectedValuationInputDetails,
	defaultCurrency,
	defaultNumberFormat,
	currentNumberFactor,
	createInputAdjustment,
	deleteInputAdjustment,
}) => {
	const { control, getValues, setValue, resetField } =
		useFormContext<CreateInputAdjustmentTableForm>();
	const {
		field: { value: inputType },
	} = useController({
		control,
		name: 'valuationInput.inputType',
	});
	// Go through setValue + shouldDirty so the form's values subscriber fires.
	// useController.onChange uses an internal setter that doesn't always propagate
	// to formState.values subscribers, so the autosave snapshot wouldn't pick the
	// new inputType up — see WAT-573 e2e regression on the enterprise toggle.
	const setInputType = (value: ValuationInputType) => {
		setValue('valuationInput.inputType', value, {
			shouldDirty: true,
			shouldTouch: true,
		});
	};

	const tValuations = useTranslationValuations();

	const { append: cashAppend, remove: cashRemove } = useFieldArray({
		control,
		name: 'cash',
	});

	const cashFields = getValues('cash');
	const workingCapitalFields = getValues('workingCapital');

	const { append: workingCapitalAppend, remove: workingCapitalRemove } = useFieldArray({
		control,
		name: 'workingCapital',
	});

	const fieldArrayMap = {
		cash: { append: cashAppend, remove: cashRemove },
		workingCapital: {
			append: workingCapitalAppend,
			remove: workingCapitalRemove,
		},
	} as const;

	if (!selectedValuationInputId) {
		return null;
	}

	const formatNumberWithFactor = (value?: HighPrecisionDecimal): string => {
		if (!value) {
			return '';
		}
		return formatDecimal({
			value: value.div(currentNumberFactor),
			maxPrecision: 2,
			minPrecision: 2,
			lockPrecision: true,
		});
	};

	const multipleLabel = tValuations(
		`multiples.${defaultNumberFormat.toLocaleLowerCase('en-GB')}_unit`
	);

	const handleUpdateInputValue = ({
		currency,
		currencyAmount,
	}: {
		currency: CurrencyType;
		currencyAmount: string;
	}) => {
		if (getValues('valuationInput.inputId') === selectedValuationInputId) {
			resetField('valuationInput.value', { defaultValue: { currency, currencyAmount } });
		}
	};

	const handleCreateInputAdjustment = async (
		adjustmentClassifierType: keyof CreateInputAdjustmentTableForm
	) => {
		const tempId = uuidv7();
		const defaultName = tValuations('new_valuation_details.default_adjustment_name');
		fieldArrayMap[adjustmentClassifierType].append({
			tempId,
			name: defaultName,
			value: {
				currency: defaultCurrency ?? CurrencyType.EUR,
				currencyAmount: '0',
			},
		});

		const classifierId =
			adjustmentClassifierType === 'cash'
				? VALUATION_ADJUSTMENT_CLASSIFIERS.CASH
				: VALUATION_ADJUSTMENT_CLASSIFIERS.WORKING_CAPITAL;

		const adjustment = await createInputAdjustment({
			valuationInputId: selectedValuationInputId,
			data: {
				name: defaultName,
				currency: defaultCurrency ?? CurrencyType.EUR,
				currencyAmount: 0,
				classifierId,
			},
		});

		if (!adjustment) {
			return;
		}
		// useFieldArray's append is async — re-read the array to find the row we
		// just inserted by tempId, then promote it with the server id.
		const index = (getValues(adjustmentClassifierType) as Array<{ tempId?: string }>).findIndex(
			(field) => field.tempId === tempId
		);
		if (index >= 0) {
			setValue(`${adjustmentClassifierType}.${index}.tempId` as 'cash.0.tempId', undefined);
			setValue(
				`${adjustmentClassifierType}.${index}.adjustmentId` as 'cash.0.adjustmentId',
				adjustment.id
			);
		}
	};

	const handleRemoveInputAdjustment = (
		valuationInputAdjustmentId: string | undefined,
		index: number,
		adjustmentClassifierType: keyof CreateInputAdjustmentTableForm
	) => {
		fieldArrayMap[adjustmentClassifierType].remove(index);

		if (valuationInputAdjustmentId) {
			deleteInputAdjustment({
				valuationInputAdjustmentId,
				valuationInputId: selectedValuationInputId,
			});
		}
	};

	const handleUpdateInputAdjustmentValue = (
		adjustmentClassifierType: 'cash' | 'workingCapital',
		adjustmentId: string | undefined,
		value: { currency: CurrencyType; currencyAmount: string }
	) => {
		const fieldArray = getValues(adjustmentClassifierType);
		const index = fieldArray.findIndex((field) => field.adjustmentId === adjustmentId);
		if (index >= 0) {
			resetField(`${adjustmentClassifierType}.${index}.value`, { defaultValue: value });
		}
	};

	const inputAdjustmentTableDisabled = inputType === ValuationInputType.EQUITY_VALUE;
	const initialValue =
		selectedValuationInputDetails.idToConvertedValues[selectedValuationInputId] ?? 0;

	return (
		<StyledCard data-testid={VALUATION_ELEMENT_ID.INPUT_TABLE}>
			<Header>
				<HeaderTop>
					<TextV2 variant="sans/25/400">{selectedValuationInputDetails.name}</TextV2>
					<Flex>
						<AdvancedSwitch
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_EQUITY_VALUE_SWITCH}
							selectedValue={inputType === ValuationInputType.EQUITY_VALUE}
							onChange={() => {
								setInputType(
									inputType === ValuationInputType.EQUITY_VALUE
										? ValuationInputType.ENTERPRISE_VALUE
										: ValuationInputType.EQUITY_VALUE
								);
							}}
							size="m"
							twoWay={true}
							leftText={
								<span
									data-testid={
										VALUATION_ELEMENT_ID.TOTAL_ENTERPRISE_VALUE_SWITCH_LABEL
									}
								>
									{tValuations(
										'target_node_details.enterprise_value_field_label',
										{
											multiple: multipleLabel,
										}
									)}
								</span>
							}
							rightText={
								<span
									data-testid={
										VALUATION_ELEMENT_ID.TOTAL_EQUITY_VALUE_SWITCH_LABEL
									}
								>
									{tValuations('target_node_details.equity_value_field_label', {
										multiple: multipleLabel,
									})}
								</span>
							}
						/>
					</Flex>
					<InputRow data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_INPUT_AMOUNT_INPUT}>
						<CurrencyInput
							key={`valuationInput.value-${selectedValuationInputId}`}
							name="valuationInput.value"
							onChange={(value) => {
								handleUpdateInputValue(value);
							}}
						/>
					</InputRow>
				</HeaderTop>
				<HeaderBottom>
					<TableCellContent $minWidth="10rem" />
					<TableCellContent $hasInputs $minWidth="7.75rem">
						<AdjustmentRow
							$variant="input"
							$columns={2}
							$gap="0rem"
							$customHeight="fit-content"
						>
							<TableHeaderTitle variant="sans/14/600">
								{tValuations(
									'new_valuation_details.new_valuation_title_adjustments_table_header'
								)}
							</TableHeaderTitle>
							<AlignRight>
								<TableHeaderTitle
									data-testid={VALUATION_ELEMENT_ID.INPUT_AMOUNT_LABEL}
									variant="sans/14/600"
								>
									{tValuations(
										'new_valuation_details.new_valuation_input_amount_table_header',
										{
											multiple: multipleLabel,
										}
									)}
								</TableHeaderTitle>
							</AlignRight>
						</AdjustmentRow>
					</TableCellContent>
					<TableCellContent $hasInputs $minWidth="5.5rem">
						<AdjustmentRow
							$variant="input"
							$columns={1}
							$gap="0rem"
							$customHeight="fit-content"
						>
							<AlignRight>
								<TableHeaderTitle
									data-testid={VALUATION_ELEMENT_ID.CONVERTED_AMOUNT_LABEL}
									variant="sans/14/600"
								>
									{tValuations(
										'new_valuation_details.new_valuation_converted_amount_table_header',
										{
											currency: defaultCurrency,
											multiple: multipleLabel,
										}
									)}
								</TableHeaderTitle>
							</AlignRight>
						</AdjustmentRow>
					</TableCellContent>
					<TableCellContent $minWidth="3.5rem">
						<ActionIconSizedPlaceholder />
					</TableCellContent>
				</HeaderBottom>
			</Header>
			<Table>
				<TableHeader>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="10rem">
							<HeaderFooterRowItem>
								<TextV2 variant="sans/14/600">
									{tValuations(
										'new_valuation_details.new_valuation_enterprise_value_field_label'
									)}
								</TextV2>
							</HeaderFooterRowItem>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $minWidth="7.75rem" />
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="5.5rem">
							<FlexContainer $direction="row" $gap="0.5rem">
								<RowContainer>
									<HeaderFooterRowItem>
										<InputAdjustmentRowConvertedAmount>
											<TextV2
												data-testid={
													VALUATION_ELEMENT_ID.TOTAL_ENTERPRISE_VALUE_CONVERTED
												}
												variant="sans/14/800"
											>
												{inputType === ValuationInputType.ENTERPRISE_VALUE
													? formatNumberWithFactor(initialValue)
													: tValuations(
															'new_valuation_details.not_applicable_value'
														)}
											</TextV2>
										</InputAdjustmentRowConvertedAmount>
									</HeaderFooterRowItem>
								</RowContainer>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs>
							<FlexContainer>
								<RowContainer>
									<InputAdjustmentRowItem $gap="0rem" $columns={1}>
										<ActionIconSizedPlaceholder />
									</InputAdjustmentRowItem>
								</RowContainer>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
				</TableHeader>
				<TableRow $disabled={inputAdjustmentTableDisabled}>
					<TableCell>
						<TableCellContent $minWidth="10rem">
							<FlexContainer $gap="0.5rem">
								<TextV2 variant="sans/14/600">
									{tValuations(
										'new_valuation_details.new_valuation_add_new_adjustment_title_cash_debt_adjustments'
									)}
								</TextV2>
								<StyledButton
									variant="outline"
									onClick={() => handleCreateInputAdjustment('cash')}
									disabled={inputAdjustmentTableDisabled}
									data-testid={VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_BUTTON}
								>
									<Icon
										variant="plus"
										size="md"
										style={{ marginRight: '0.5rem' }}
									/>
									{tValuations('new_valuation_details.add_new')}
								</StyledButton>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="7.75rem">
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									cashFields.map((field, index) => (
										<RowContainer
											data-testid={`${VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_ROW}_${index}`}
											key={field.adjustmentId ?? field.tempId}
										>
											<InputAdjustmentRowItem>
												<TextInput
													data-testid={
														VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_ADJUSTMENT_LABEL
													}
													name={`cash.${index}.name`}
													control={control}
													defaultValue={field.name}
													size="sm"
													placeholder={tValuations(
														'new_valuation_details.adjustment_name_placeholder'
													)}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												/>
												<CurrencyInput
													name={`cash.${index}.value`}
													onChange={(value) =>
														handleUpdateInputAdjustmentValue(
															'cash',
															field.adjustmentId,
															value
														)
													}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												/>
											</InputAdjustmentRowItem>
										</RowContainer>
									))}
								{!inputAdjustmentTableDisabled && cashFields.length > 0 && (
									<RowContainer>
										<TotalRow>
											<TextV2 variant="sans/14/600">
												{tValuations(
													'new_valuation_details.total_cash_value_field_label'
												)}
											</TextV2>
										</TotalRow>
									</RowContainer>
								)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="5.5rem">
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									cashFields.map((field) => (
										<RowContainer
											key={field.adjustmentId ?? field.tempId}
											$showLastChildBorder
										>
											<InputAdjustmentRowTotal>
												<InputAdjustmentRowConvertedAmount>
													<TextV2
														data-testid={makeComplexDataTestId(
															VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_CONVERTED_AMOUNT,
															field.name
														)}
														variant="sans/14/400"
													>
														{formatNumberWithFactor(
															selectedValuationInputDetails
																.idToConvertedValues[
																field.adjustmentId ?? ''
															]
														)}
													</TextV2>
												</InputAdjustmentRowConvertedAmount>
											</InputAdjustmentRowTotal>
										</RowContainer>
									))}
								{!inputAdjustmentTableDisabled && cashFields.length > 0 && (
									<RowContainer>
										<TotalRow $columns={1}>
											<AlignRight>
												<TextV2
													data-testid={
														VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_ADJUSTMENT_TOTAL_VALUE
													}
													variant="sans/14/600"
												>
													{formatNumberWithFactor(
														selectedValuationInputDetails.totalCashConverted
													)}
												</TextV2>
											</AlignRight>
										</TotalRow>
									</RowContainer>
								)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs>
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									cashFields.map((field, index) => (
										<RowContainer
											key={field.adjustmentId ?? field.tempId}
											$showLastChildBorder
										>
											<InputAdjustmentRowItem $gap="0rem" $columns={1}>
												<ActionIcon
													data-testid={`${VALUATION_ELEMENT_ID.CASH_DEBT_ADJUSTMENT_BUTTON_DELETE}_${index}`}
													size="md"
													onClick={() => {
														handleRemoveInputAdjustment(
															field.adjustmentId,
															index,
															'cash'
														);
													}}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												>
													<Icon variant="delete" size="md" />
												</ActionIcon>
											</InputAdjustmentRowItem>
										</RowContainer>
									))}
								{(inputAdjustmentTableDisabled || cashFields.length === 0) && (
									<RowContainer>
										<InputAdjustmentRowItem $gap="0rem" $columns={1}>
											<ActionIconSizedPlaceholder />
										</InputAdjustmentRowItem>
									</RowContainer>
								)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
				</TableRow>
				<TableRow $disabled={inputAdjustmentTableDisabled}>
					<TableCell>
						<TableCellContent $minWidth="10rem">
							<FlexContainer $gap="0.5rem">
								<TextV2 variant="sans/14/600">
									{tValuations(
										'new_valuation_details.new_valuation_add_new_adjustment_title_net_working_capital_adjustments'
									)}
								</TextV2>
								<StyledButton
									variant="outline"
									onClick={() => handleCreateInputAdjustment('workingCapital')}
									disabled={inputAdjustmentTableDisabled}
									data-testid={
										VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_BUTTON
									}
								>
									<Icon
										variant="plus"
										size="md"
										style={{ marginRight: '0.5rem' }}
									/>
									{tValuations('new_valuation_details.add_new')}
								</StyledButton>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="7.75rem">
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									workingCapitalFields.map((field, index) => (
										<RowContainer
											data-testid={`${VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_ROW}_${index}`}
											key={field.adjustmentId ?? field.tempId}
										>
											<InputAdjustmentRowItem>
												<TextInput
													data-testid={`${VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_LABEL}`}
													name={`workingCapital.${index}.name`}
													control={control}
													defaultValue={field.name}
													size="sm"
													placeholder={tValuations(
														'new_valuation_details.adjustment_name_placeholder'
													)}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												/>
												<CurrencyInput
													name={`workingCapital.${index}.value`}
													onChange={(value) =>
														handleUpdateInputAdjustmentValue(
															'workingCapital',
															field.adjustmentId,
															value
														)
													}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												/>
											</InputAdjustmentRowItem>
										</RowContainer>
									))}
								{!inputAdjustmentTableDisabled &&
									workingCapitalFields.length > 0 && (
										<RowContainer>
											<TotalRow>
												<TextV2 variant="sans/14/600">
													{tValuations(
														'new_valuation_details.total_working_capital_value_field_label'
													)}
												</TextV2>
											</TotalRow>
										</RowContainer>
									)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="5.5rem">
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									workingCapitalFields.map((field) => (
										<RowContainer
											key={field.adjustmentId ?? field.tempId}
											$showLastChildBorder
										>
											<InputAdjustmentRowTotal>
												<InputAdjustmentRowConvertedAmount>
													<TextV2
														data-testid={makeComplexDataTestId(
															VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_CONVERTED_AMOUNT,
															field.name
														)}
														variant="sans/14/400"
													>
														{formatNumberWithFactor(
															selectedValuationInputDetails
																.idToConvertedValues[
																field.adjustmentId ?? ''
															]
														)}
													</TextV2>
												</InputAdjustmentRowConvertedAmount>
											</InputAdjustmentRowTotal>
										</RowContainer>
									))}
								{!inputAdjustmentTableDisabled &&
									workingCapitalFields.length > 0 && (
										<RowContainer>
											<TotalRow $columns={1}>
												<AlignRight>
													<TextV2
														data-testid={
															VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_TOTAL_VALUE
														}
														variant="sans/14/600"
													>
														{formatNumberWithFactor(
															selectedValuationInputDetails.totalWorkingCapitalConverted
														)}
													</TextV2>
												</AlignRight>
											</TotalRow>
										</RowContainer>
									)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs>
							<FlexContainer>
								{!inputAdjustmentTableDisabled &&
									workingCapitalFields.map((field, index) => (
										<RowContainer
											key={field.adjustmentId ?? field.tempId}
											$showLastChildBorder
										>
											<InputAdjustmentRowItem $gap="0rem" $columns={1}>
												<ActionIcon
													data-testid={`${VALUATION_ELEMENT_ID.NET_WORKING_CAPITAL_ADJUSTMENT_BUTTON_DELETE}_${index}`}
													size="md"
													onClick={() => {
														handleRemoveInputAdjustment(
															field.adjustmentId,
															index,
															'workingCapital'
														);
													}}
													disabled={
														inputAdjustmentTableDisabled ||
														!field.adjustmentId
													}
												>
													<Icon variant="delete" size="md" />
												</ActionIcon>
											</InputAdjustmentRowItem>
										</RowContainer>
									))}
								{(inputAdjustmentTableDisabled ||
									workingCapitalFields.length === 0) && (
									<RowContainer>
										<InputAdjustmentRowItem $gap="0rem" $columns={1}>
											<ActionIconSizedPlaceholder />
										</InputAdjustmentRowItem>
									</RowContainer>
								)}
							</FlexContainer>
						</TableCellContent>
					</TableCell>
				</TableRow>
				<TableRow $isFooter>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="10rem">
							<HeaderFooterRowItem>
								<TextV2 variant="sans/14/600">
									{tValuations(
										'new_valuation_details.new_valuation_equity_value_field_label'
									)}
								</TextV2>
							</HeaderFooterRowItem>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $minWidth="7.75rem" />
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs $minWidth="5.5rem">
							<FlexContainer $direction="row" $gap="0.5rem">
								<RowContainer>
									<HeaderFooterRowItem>
										<InputAdjustmentRowConvertedAmount>
											<TextV2
												data-testid={
													VALUATION_ELEMENT_ID.TOTAL_EQUITY_VALUE_ADJUSTMENT_CONVERTED
												}
												variant="sans/14/800"
											>
												{formatNumberWithFactor(
													selectedValuationInputDetails.equityValue
												)}
											</TextV2>
										</InputAdjustmentRowConvertedAmount>
									</HeaderFooterRowItem>
								</RowContainer>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
					<TableCell>
						<TableCellContent $hasInputs>
							<FlexContainer>
								<RowContainer>
									<InputAdjustmentRowItem $gap="0rem" $columns={1}>
										<ActionIconSizedPlaceholder />
									</InputAdjustmentRowItem>
								</RowContainer>
							</FlexContainer>
						</TableCellContent>
					</TableCell>
				</TableRow>
			</Table>
		</StyledCard>
	);
};
